import { useSetAtom } from 'jotai';

import { isLoggedInAtom, userInfoAtom, useResetQueryAtoms } from 'app/atoms';
import { log, recordError } from 'app/lib/monitoring';
import { StorageKey, storage } from 'app/lib/storage';
import { useQueryClient } from '@tanstack/react-query';
import { clearLastExecution } from 'app/lib/query';
import type { User } from 'app/types/user';
import { userKeys } from 'app/network/keys';
import { updateSessionState } from 'app/lib/axios/session-state';
import { clearPersistedQueryCache } from 'app/lib/query-persistence';

export interface SignInResponse {
  AccessToken: string;
  RefreshToken?: string;
  IdToken: string;
}

interface LoginRoutineOptions {
  beforeSetLoggedIn?: () => Promise<User | undefined>;
}

export function useLoginRoutine() {
  const setIsLoggedIn = useSetAtom(isLoggedInAtom);
  const setUserInfo = useSetAtom(userInfoAtom);
  const resetQueryAtoms = useResetQueryAtoms();
  const queryClient = useQueryClient();

  /**
   * Executes the minimal set of instructions required to proceed to the login
   */
  return async (response: SignInResponse, options?: LoginRoutineOptions) => {
    await storage.saveString(StorageKey.AccessToken, response.AccessToken);
    if (response.RefreshToken) {
      await storage.saveString(StorageKey.RefreshToken, response.RefreshToken);
    } else {
      await storage.clear(StorageKey.RefreshToken);
      recordError(new Error('RefreshToken missing from login response'));
    }
    updateSessionState({ accessToken: response.AccessToken, refreshToken: response.RefreshToken ?? null });

    const loginAsUser = await options?.beforeSetLoggedIn?.();

    resetQueryAtoms();
    clearLastExecution();
    queryClient.clear(); // a logged-in user might have access to different categories etc.
    try {
      await clearPersistedQueryCache();
    } catch (error) {
      recordError(error);
    }
    if (loginAsUser) {
      queryClient.setQueryData(userKeys.detail('self'), loginAsUser);
      setUserInfo(loginAsUser);
      await storage.saveString(StorageKey.CurrentUserId, loginAsUser.id);
      updateSessionState({ currentUserId: loginAsUser.id });
    }
    setIsLoggedIn(true);
    log('User was logged in with Cognito');
  };
}
